// Click-to-edit one sentence (clinician request 04/11): the note's Markdown
// cut into the units the sentence editor opens. A list item or heading line
// is one unit; a paragraph line is cut into sentences ('.', '?', '!' and
// '。！？', not after a title such as "Dr.", inside a decimal such as "7.6",
// or before a lowercase word, as in "500 mg. twice daily").
//
// The editor shows a unit as plain text (no "**", no "[verify]"); an edit is
// written back into the Markdown around the formatting it did not touch
// (editNoteUnit). An empty edit removes the unit and tidies the line
// (removeNoteUnit).

import { extractVerifyFlags } from "./verifyFlags";

export interface TextSpan {
  start: number;
  end: number;
}

// ---------------------------------------------------------------------------
// Sentences in plain text

const TERMINATORS = /[.?!。！？]/u;
const CJK_TERMINATORS = /[。！？]/u;
// Closing marks a sentence may end with ("…daily.)", "**Plan.**").
const CLOSERS = /[)\]"'”’»*_]/u;
const WORD = /[\p{L}\p{N}]/u;
// Titles a name follows: never the end of a sentence.
const TITLES = new Set([
  "dr",
  "dra",
  "drs",
  "mr",
  "mrs",
  "ms",
  "mx",
  "mme",
  "mlle",
  "prof",
  "sr",
  "sra",
  "srta",
  "jr",
  "st",
  "vs",
  "cf",
  "approx",
]);

const trimSpan = (text: string, start: number, end: number): TextSpan => {
  let from = start;
  let to = end;
  while (from < to && /\s/u.test(text[from] ?? "")) from += 1;
  while (to > from && /\s/u.test(text[to - 1] ?? "")) to -= 1;
  return { start: from, end: to };
};

// A "." at `dot` (followed by space or the end) ends the sentence unless the
// word before it is a title, the last letter of a dotted abbreviation
// ("e.g.", "a.m."), or the next word starts in lowercase.
const dotEndsSentence = (text: string, dot: number, after: number): boolean => {
  let from = dot;
  while (from > 0 && WORD.test(text[from - 1] ?? "")) from -= 1;
  const word = text.slice(from, dot);
  if (word) {
    if (TITLES.has(word.toLowerCase())) return false;
    if (word.length === 1 && /\p{L}/u.test(word) && text[from - 1] === ".")
      return false;
  }
  let next = after;
  while (next < text.length && /\s/u.test(text[next] ?? "")) next += 1;
  if (next >= text.length) return true;
  return !/\p{Ll}/u.test(text[next] ?? "");
};

// The sentences of `text`, trimmed, in order.
export const segmentSentences = (text: string): TextSpan[] => {
  const spans: TextSpan[] = [];
  let start = 0;
  const push = (end: number): void => {
    const span = trimSpan(text, start, end);
    if (span.end > span.start) spans.push(span);
    start = end;
  };
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i] ?? "";
    if (!TERMINATORS.test(char)) continue;
    let end = i + 1;
    while (end < text.length && TERMINATORS.test(text[end] ?? "")) end += 1;
    const run = text.slice(i, end);
    while (end < text.length && CLOSERS.test(text[end] ?? "")) end += 1;
    const cjk = CJK_TERMINATORS.test(run);
    const atEnd = end >= text.length;
    const spaced = !atEnd && /\s/u.test(text[end] ?? "");
    if (!atEnd && !spaced && !cjk) {
      i = end - 1;
      continue;
    }
    if (!cjk && run[0] === "." && !dotEndsSentence(text, i, end)) {
      i = end - 1;
      continue;
    }
    push(end);
    i = end - 1;
  }
  push(text.length);
  return spans;
};

// ---------------------------------------------------------------------------
// Lines and units of a note

export type NoteLineKind = "blank" | "heading" | "item" | "paragraph" | "skip";

export interface NoteLine {
  // [start, end) without the newline.
  start: number;
  end: number;
  // Where the text starts, after a bullet, number, "#" or ">".
  contentStart: number;
  kind: NoteLineKind;
}

export interface NoteUnit extends TextSpan {
  line: NoteLine;
}

const LIST_PREFIX =
  /^[ \t]*(?:(?:[-*+•·◦▪‣]|\d{1,3}[.)])[ \t]+(?:\[[ xX]\][ \t]+)?|☐[ \t]*)/u;
const HEADING_PREFIX = /^[ \t]*#{1,6}[ \t]+/u;
const QUOTE_PREFIX = /^[ \t]*>[ \t]?/u;
const RULE = /^[ \t]*([-*_])(?:[ \t]*\1){2,}[ \t]*$/u;
const FENCE = /^[ \t]*(?:```|~~~)/u;
const TABLE = /^[ \t]*\|/u;

export const noteLines = (text: string): NoteLine[] => {
  const lines: NoteLine[] = [];
  let fenced = false;
  let start = 0;
  while (start <= text.length) {
    const newline = text.indexOf("\n", start);
    let end = newline === -1 ? text.length : newline;
    if (text[end - 1] === "\r") end -= 1;
    const raw = text.slice(start, end);
    let kind: NoteLineKind = "paragraph";
    let contentStart = start;
    if (FENCE.test(raw)) {
      fenced = !fenced;
      kind = "skip";
    } else if (fenced || TABLE.test(raw) || RULE.test(raw)) kind = "skip";
    else if (!raw.trim()) kind = "blank";
    else {
      const list = LIST_PREFIX.exec(raw);
      const heading = HEADING_PREFIX.exec(raw);
      const quote = QUOTE_PREFIX.exec(raw);
      if (list) {
        kind = "item";
        contentStart = start + list[0].length;
      } else if (heading) {
        kind = "heading";
        contentStart = start + heading[0].length;
      } else {
        if (quote) contentStart = start + quote[0].length;
        while (contentStart < end && /[ \t]/.test(text[contentStart] ?? ""))
          contentStart += 1;
      }
    }
    lines.push({ start, end, contentStart, kind });
    if (newline === -1) break;
    start = newline + 1;
  }
  return lines;
};

// The units of one line: the whole line for a list item or heading, its
// sentences for a paragraph line.
export const lineUnits = (text: string, line: NoteLine): NoteUnit[] => {
  if (line.kind === "blank" || line.kind === "skip") return [];
  if (line.kind !== "paragraph") {
    const span = trimSpan(text, line.contentStart, line.end);
    return span.end > span.start ? [{ ...span, line }] : [];
  }
  const content = text.slice(line.contentStart, line.end);
  return segmentSentences(content).map((span) => ({
    start: line.contentStart + span.start,
    end: line.contentStart + span.end,
    line,
  }));
};

export const noteUnits = (text: string): NoteUnit[] =>
  noteLines(text).flatMap((line) => lineUnits(text, line));

// ---------------------------------------------------------------------------
// Plain text of a stretch of Markdown, with where each character came from

interface Hidden extends TextSpan {
  kind: "marker" | "delimiter" | "escape";
  // Index of the matching delimiter in `hidden`, or -1.
  pair: number;
}

export interface PlainText {
  plain: string;
  // Source index of each plain character; map[plain.length] is `to`.
  map: number[];
  hidden: Hidden[];
}

const MARKER = /[ \t]*\\?\[verify\\?\]/iy;
const LINK = /\[([^\]\n]*)\]\(([^)\n]*)\)/y;

// The text as the note shows it: no "**", "_", "`", link targets, escapes
// or "[verify]" markers (with the spaces before them).
export const plainText = (
  text: string,
  from = 0,
  to = text.length,
): PlainText => {
  const hidden: Hidden[] = [];
  const map: number[] = [];
  let plain = "";
  const open = new Map<string, number>();
  let linkClose: { start: number; end: number; open: number } | null = null;
  const hide = (
    start: number,
    end: number,
    kind: Hidden["kind"],
    key?: string,
  ): void => {
    const index = hidden.length;
    hidden.push({ start, end, kind, pair: -1 });
    if (key === undefined) return;
    const opener = open.get(key);
    if (opener === undefined) open.set(key, index);
    else {
      const first = hidden[opener];
      const second = hidden[index];
      if (first && second) {
        first.pair = index;
        second.pair = opener;
      }
      open.delete(key);
    }
  };
  for (let i = from; i < to; ) {
    if (linkClose && i === linkClose.start) {
      const index = hidden.length;
      hidden.push({
        start: i,
        end: linkClose.end,
        kind: "delimiter",
        pair: linkClose.open,
      });
      const opener = hidden[linkClose.open];
      if (opener) opener.pair = index;
      i = linkClose.end;
      linkClose = null;
      continue;
    }
    MARKER.lastIndex = i;
    const marker = MARKER.exec(text);
    if (marker && i + marker[0].length <= to) {
      hide(i, i + marker[0].length, "marker");
      i += marker[0].length;
      continue;
    }
    const char = text[i] ?? "";
    const next = text[i + 1] ?? "";
    if (char === "\\" && i + 1 < to && /[\\[\]*_`]/.test(next)) {
      hide(i, i + 1, "escape");
      plain += next;
      map.push(i + 1);
      i += 2;
      continue;
    }
    if (char === "[" && !linkClose) {
      LINK.lastIndex = i;
      const link = LINK.exec(text);
      if (link && i + link[0].length <= to) {
        linkClose = {
          start: i + 1 + (link[1]?.length ?? 0),
          end: i + link[0].length,
          open: hidden.length,
        };
        hidden.push({ start: i, end: i + 1, kind: "delimiter", pair: -1 });
        i += 1;
        continue;
      }
    }
    if ((char === "*" || char === "_") && next === char && i + 1 < to) {
      hide(i, i + 2, "delimiter", char + char);
      i += 2;
      continue;
    }
    if (char === "`") {
      hide(i, i + 1, "delimiter", "`");
      i += 1;
      continue;
    }
    if (char === "*" || char === "_") {
      const before = i > from ? (text[i - 1] ?? "") : "";
      const after = i + 1 < to ? next : "";
      const opens = !WORD.test(before) && !!after && !/\s/u.test(after);
      const closes = !!before && !/\s/u.test(before) && !WORD.test(after);
      if (open.has(char) ? closes : opens) {
        hide(i, i + 1, "delimiter", char);
        i += 1;
        continue;
      }
    }
    plain += char;
    map.push(i);
    i += 1;
  }
  map.push(to);
  return { plain, map, hidden };
};

// What the sentence editor shows for a unit.
export const unitText = (text: string, unit: TextSpan): string =>
  plainText(text, unit.start, unit.end).plain.trim();

// ---------------------------------------------------------------------------
// Removing a unit, and tidying what is left

const BLANK = /[ \t]/;

// Removes a whole line with its newline; a blank line left next to another
// blank line goes too.
const removeLine = (text: string, line: NoteLine): string => {
  let start = line.start;
  let end = line.end;
  if (text[end] === "\r") end += 1;
  if (text[end] === "\n") end += 1;
  else if (start > 0) {
    // The last line: the newline before it goes.
    start -= 1;
    if (text[start - 1] === "\r") start -= 1;
  }
  let next = text.slice(0, start) + text.slice(end);
  // Two blank lines meet where the line was.
  const at = Math.min(start, next.length);
  const lineStart = next.lastIndexOf("\n", at - 1) + 1;
  const prevBlank =
    lineStart > 0 &&
    !next.slice(next.lastIndexOf("\n", lineStart - 2) + 1, lineStart).trim();
  const lineEnd = next.indexOf("\n", at);
  const currentBlank = !next
    .slice(lineStart, lineEnd === -1 ? next.length : lineEnd)
    .trim();
  if (prevBlank && currentBlank && lineEnd !== -1)
    next = next.slice(0, lineStart) + next.slice(lineEnd + 1);
  // No blank lines left at the very start or end.
  return next.replace(/^(?:[ \t]*\n)+/, "").replace(/(?:\n[ \t]*)+$/, "");
};

const LEADING_JOINER =
  /^[ \t]*(?:[,;:.][ \t]*)?(?:(?:and|or|but|y|e|et|ou|o)[ \t]+)?/iu;

// Tidies the line around `at`, where text was just taken out: no doubled
// spaces or space before punctuation, no orphan ", " / "and" at the start
// of the line's text (which then starts in uppercase if it did before).
export const tidyJoin = (
  text: string,
  at: number,
  { capitalize = false }: { capitalize?: boolean } = {},
): string => {
  const lines = noteLines(text);
  const line =
    lines.find((item) => item.start <= at && at <= item.end) ?? lines.at(-1);
  if (!line) return text;
  const contentStart = Math.min(line.contentStart, line.end);
  let next = text;
  if (!next.slice(contentStart, at).trim()) {
    // The removed text started the line's text.
    const rest = next.slice(contentStart, line.end);
    const joiner = LEADING_JOINER.exec(rest);
    let cut = joiner?.[0].length ?? 0;
    // Only a conjunction followed by more text goes.
    if (cut && !rest.slice(cut).trim()) cut = rest.length;
    let body = rest.slice(cut);
    if (capitalize) body = body.replace(/^\p{Ll}/u, (c) => c.toUpperCase());
    // One space after the bullet.
    const prefix = next
      .slice(line.start, contentStart)
      .replace(/(\S)[ \t]{2,}$/u, "$1 ");
    next = next.slice(0, line.start) + prefix + body + next.slice(line.end);
    return next;
  }
  let start = at;
  let end = at;
  while (start > contentStart && BLANK.test(next[start - 1] ?? "")) start -= 1;
  while (end < line.end && BLANK.test(next[end] ?? "")) end += 1;
  if (start === end) return next;
  const following = next[end] ?? "";
  const gap = end >= line.end || /[.,;:!?)]/.test(following) ? "" : " ";
  return next.slice(0, start) + gap + next.slice(end);
};

// A line left with only a bullet, a number, a heading mark or a label
// ("Allergies -", "To discuss:").
export const isEmptyLine = (line: string): boolean =>
  /^[\s\-*+•#>\d.)]*(?:(?:\*\*|__)?[\p{L} ]{1,40}(?:\*\*|__)?\s?[-–:](?:\*\*|__)?)?[\s.]*$/u.test(
    line,
  );

// The note without `unit`. The line goes when nothing else is left on it.
export const removeNoteUnit = (text: string, unit: NoteUnit): string => {
  const { line } = unit;
  const before = text.slice(line.contentStart, unit.start);
  const after = text.slice(unit.end, line.end);
  if (!before.trim() && !after.trim()) return removeLine(text, line);
  let start = unit.start;
  let end = unit.end;
  if (after.trim())
    while (end < line.end && BLANK.test(text[end] ?? "")) end += 1;
  else
    while (start > line.contentStart && BLANK.test(text[start - 1] ?? ""))
      start -= 1;
  return text.slice(0, start) + text.slice(end);
};

// ---------------------------------------------------------------------------
// Editing a unit

const EMPTY_PAIR = /(\*\*|__)\1/g;

// The note with `unit` replaced by `value` (plain text from the editor).
// Formatting outside the changed words stays; a "[verify]" marker inside
// them goes (the value was edited, so it is reviewed). Empty removes it.
export const editNoteUnit = (
  text: string,
  unit: NoteUnit,
  value: string,
): string => {
  const typed = value.replace(/\r\n?/g, "\n").trim();
  if (!typed) return removeNoteUnit(text, unit);
  const { plain, map, hidden } = plainText(text, unit.start, unit.end);
  const lead = plain.length - plain.trimStart().length;
  const trail = plain.length - plain.trimEnd().length;
  // New lines: a list item continues as another item; a heading stays one
  // line; a paragraph gets a line break.
  const { line } = unit;
  const prefix = text.slice(line.start, line.contentStart);
  const lined =
    line.kind === "item"
      ? typed.replace(/[ \t]*\n\s*/g, `\n${prefix}`)
      : line.kind === "heading"
        ? typed.replace(/\s*\n\s*/g, " ")
        : typed.replace(/[ \t]*\n[ \t]*/g, "\n");
  const next = plain.slice(0, lead) + lined + plain.slice(plain.length - trail);
  if (next === plain) return text;
  let p = 0;
  while (p < plain.length && p < next.length && plain[p] === next[p]) p += 1;
  let s = 0;
  while (
    s < plain.length - p &&
    s < next.length - p &&
    plain[plain.length - 1 - s] === next[next.length - 1 - s]
  )
    s += 1;
  const e = plain.length - s;
  const rawStart = map[p] ?? unit.end;
  const rawEnd = e === p ? rawStart : (map[e - 1] ?? rawStart) + 1;
  const middle = next.slice(p, next.length - s);
  // Delimiters inside the changed words: a pair inside goes, one whose
  // partner is outside stays (an opener before the new words, a closer
  // after them); markers go.
  let openers = "";
  let closers = "";
  hidden.forEach((item) => {
    if (item.start < rawStart || item.end > rawEnd) return;
    if (item.kind !== "delimiter" || item.pair === -1) return;
    const partner = hidden[item.pair];
    if (!partner) return;
    if (partner.start >= rawStart && partner.end <= rawEnd) return;
    const raw = text.slice(item.start, item.end);
    if (partner.start < rawStart) closers += raw;
    else openers += raw;
  });
  const core = middle.trim();
  const leading = middle.slice(0, middle.length - middle.trimStart().length);
  const trailing = middle.slice(middle.trimEnd().length);
  const replacement = core
    ? leading + openers + core + closers + trailing
    : leading + closers + openers + trailing;
  // A flagged value the edit changed is reviewed: its marker goes, also
  // when the marker itself is outside the changed words.
  const touched = extractVerifyFlags(text).filter(
    (flag) =>
      flag.valueEnd > flag.valueStart &&
      flag.valueStart <= rawEnd &&
      rawStart <= flag.valueEnd,
  );
  const markers = hidden.filter(
    (item) =>
      item.kind === "marker" &&
      (item.end <= rawStart || item.start >= rawEnd) &&
      touched.some((flag) => flag.markerEnd === item.end),
  );
  const cuts = [
    { start: rawStart, end: rawEnd, text: replacement },
    ...markers.map((item) => ({ start: item.start, end: item.end, text: "" })),
  ].sort((a, b) => b.start - a.start);
  let out = text;
  for (const cut of cuts)
    out = out.slice(0, cut.start) + cut.text + out.slice(cut.end);
  const shift = markers
    .filter((item) => item.end <= rawStart)
    .reduce((sum, item) => sum + item.end - item.start, 0);
  // An emphasis pair left with nothing inside ("****") goes.
  const windowStart = Math.max(line.start, rawStart - shift - 2);
  const windowEnd = rawStart - shift + replacement.length + 2;
  out =
    out.slice(0, windowStart) +
    out.slice(windowStart, windowEnd).replace(EMPTY_PAIR, "") +
    out.slice(windowEnd);
  return out;
};
